(function (root, factory) {
  var analytics = factory(root || {});

  if (typeof module === 'object' && module.exports) {
    module.exports = analytics;
    return;
  }

  root.DecoAnalytics = analytics;
  analytics.boot();
})(typeof window !== 'undefined' ? window : undefined, function (root) {
  'use strict';

  var documentRef = root.document;
  var CONTEXT_ID = 'dg-posthog-context';
  var SESSION_KEY = 'dg_ph_engagement_v1';
  var DISTINCT_ID_KEY = 'dg_ph_distinct_id';
  var FIRST_SEEN_KEY = 'dg_ph_first_seen_at';
  var LAST_PURCHASE_KEY = 'dg_ph_last_purchase_at';
  var CUSTOMER_KEY = 'dg_ph_customer_id';
  var HIGH_INTENT_KEY = 'dg_ph_high_intent_sent';
  var DELIVERY_QUOTE_KEY = 'dg_ph_delivery_quote_v1';
  var NEWSLETTER_FUNNEL_KEY = 'dg_ph_newsletter_funnel_v1';
  var IDENTITY_PUBLISH_KEY = 'dg_ph_identity_pub_v1';
  var IDENTITY_SETTLE_MS = 6000;
  var IDENTITY_REPUBLISH_MS = 24 * 60 * 60 * 1000;
  var NEWSLETTER_FUNNEL_MAX_AGE_MS = 30 * 60 * 1000;
  var NEWSLETTER_RESUBMIT_DEDUPE_MS = 5000;
  var SESSION_MAX_IDLE_MS = 30 * 60 * 1000;
  var PURCHASE_COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000;
  var EMAIL_PATTERN = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
  // A phone number stands on its own: not preceded or followed by another digit.
  // Without that boundary the pattern matched inside Shopify product and variant
  // ids ("7813466052908" -> "78134[redacted-phone]"), garbling about half of the
  // product ids PostHog received until 25.9.2026. No lookbehind: older iOS Safari
  // rejects it and the whole file would stop running.
  var ISRAEL_PHONE_PATTERN = /(^|[^\d])((?:\+972|0)(?:[\s().-]*\d){8,9})(?!\d)/g;
  var SENSITIVE_KEY_PATTERN = /(?:^|_)(?:address|authorization|card|credit|email|first_name|last_name|note|password|payment|phone|postcode|postal|secret|token|value|zip)(?:$|_)/i;
  // The same list without "value": a number under a *_value key is a measurement
  // (web vitals, price x quantity), never typed text.
  var SENSITIVE_NUMBER_KEY_PATTERN = /(?:^|_)(?:address|authorization|card|credit|email|first_name|last_name|note|password|payment|phone|postcode|postal|secret|token|zip|customer_id)(?:$|_)/i;
  var URL_KEY_PATTERN = /(?:^|_)(?:current_url|href|referrer|referring_url|url)(?:$|_)/i;
  var INJECTED_BRIDGE_EXCEPTION_FINGERPRINT =
    '5ebbf847bf43b546f54f2cc07a954ae88155bb473ede13b26fb827d258ad9ad099bee169a623427366b8e870d703ae531443cfeef93ae844596262819ac34d81';
  var MASK_TEXT_SELECTOR = [
    '.customer',
    '.account',
    '.addresses',
    '[data-customer]',
    '[class*="address"]',
    '[id*="address"]',
    '.cart__note',
    '.gift-card-recipient-form',
    '.dg-delivery-popup',
    '#delivery-popup',
    '[data-sensitive]'
  ].join(', ');
  var BLOCK_SELECTOR = [
    '.ph-no-capture',
    '.shopify-payment-button',
    '[data-ph-no-capture]',
    'iframe[src*="payment"]'
  ].join(', ');
  var IGNORED_CLICK_SELECTOR = [
    '#shopify-pc__banner',
    '#shopify-pc__prefs',
    '[data-shopify-privacy]',
    '[class*="cookie"]',
    '[id*="cookie"]',
    '.PostHogSurvey',
    '[class*="posthog-survey"]',
    '.ph-no-capture'
  ].join(', ');

  var state = {
    config: null,
    analyticsAllowed: false,
    marketingAllowed: false,
    initialized: false,
    initializing: false,
    initialEventsSent: false,
    listenersAttached: false,
    lastInteractionAt: Date.now(),
    activeSeconds: 0,
    meaningfulClicks: 0,
    productViews: 0,
    searches: 0,
    addToCart: 0,
    checkoutClicks: 0,
    pageviews: 0,
    maxScrollDepth: 0,
    purchasedRecently: false,
    highIntentFired: false,
    metaHighIntentSent: false,
    highIntentEventId: null,
    highIntentReason: null,
    lastUpdatedAt: Date.now(),
    lastSummaryActiveSeconds: 0,
    lastSummaryClicks: 0,
    lastAddCapture: null,
    opaqueScriptErrors: 0,
    rageClicks: [],
    lastRageClickAt: 0,
    visitorType: 'unknown'
  };

  // Journey milestones mirrored into Microsoft Clarity as custom events and tags,
  // so a recording can be filtered by the exact point where the shopper stopped.
  // Only coarse labels travel: no names, contact details, addresses or cart notes.
  var CLARITY_MIRROR = {
    'Product Added': function () {
      return { event: 'dg_add_to_cart' };
    },
    'Cart Viewed': function () {
      return { event: 'dg_cart_viewed' };
    },
    'DG Cart Form Started': function () {
      return { event: 'dg_cart_form_started' };
    },
    'DG Checkout Button Clicked': function (properties) {
      return { event: 'dg_checkout_click', tags: { dg_checkout_click_from: properties.button_location } };
    },
    'DG Checkout Blocked': function (properties) {
      return {
        event: 'dg_checkout_blocked',
        tags: { dg_block_reason: properties.block_reason, dg_missing_fields: properties.missing_fields }
      };
    },
    'DG Checkout Gate Passed': function (properties) {
      return { event: 'dg_checkout_gate_passed', tags: { dg_delivery_mode: properties.delivery_mode } };
    },
    'DG Delivery Quote Viewed': function (properties) {
      return {
        tags: {
          dg_delivery_cost: properties.delivery_cost_bucket,
          dg_delivery_status: properties.delivery_status
        }
      };
    },
    'DG Delivery Check Failed': function (properties) {
      return { event: 'dg_delivery_check_failed', tags: { dg_delivery_error: properties.failure_reason } };
    },
    'DG Cart Form Abandoned': function (properties) {
      return { event: 'dg_cart_form_abandoned', tags: { dg_form_last_block: properties.last_block_reason } };
    },
    'DG High Intent': function (properties) {
      return { event: 'dg_high_intent', tags: { dg_intent_reason: properties.intent_reason } };
    }
  };

  function safeJsonParse(value, fallback) {
    try {
      return JSON.parse(value);
    } catch (error) {
      return fallback;
    }
  }

  function parseConfig() {
    if (!documentRef) return null;
    var node = documentRef.getElementById(CONTEXT_ID);
    if (!node) return null;
    return safeJsonParse(node.textContent || '{}', null);
  }

  function redactText(value) {
    if (typeof value !== 'string') return value;
    return value
      .replace(EMAIL_PATTERN, '[redacted-email]')
      .replace(ISRAEL_PHONE_PATTERN, '$1[redacted-phone]')
      .slice(0, 500);
  }

  function sanitizeUrl(value) {
    if (typeof value !== 'string' || !value) return value;

    try {
      var parsed = new URL(value, root.location && root.location.origin ? root.location.origin : 'https://decogarden.co.il');
      return parsed.origin === 'https://decogarden.co.il' || /^https?:$/.test(parsed.protocol)
        ? parsed.origin + parsed.pathname
        : parsed.pathname;
    } catch (error) {
      return value.split('?')[0].split('#')[0].slice(0, 500);
    }
  }

  function isActionableResourceUrl(value) {
    if (typeof value !== 'string' || !value || /^(?:data|blob):/i.test(value)) return false;

    try {
      var parsed = new URL(value, root.location && root.location.origin ? root.location.origin : 'https://decogarden.co.il');
      var hostname = parsed.hostname.toLowerCase();
      if (parsed.pathname.indexOf('/cdn/shopifycloud/checkout-web/') === 0) return false;
      if (hostname === 'decogarden.co.il' || hostname.endsWith('.decogarden.co.il')) return true;
      if (hostname.endsWith('.myshopify.com')) return true;
      return hostname === 'cdn.shopify.com' && parsed.pathname.indexOf('/s/files/') === 0;
    } catch (error) {
      return value.charAt(0) === '/';
    }
  }

  function isActionableResourceElement(target, pageUrl) {
    if (!target || !target.tagName) return false;

    var tagName = String(target.tagName).toUpperCase();
    var getAttribute = typeof target.getAttribute === 'function'
      ? target.getAttribute.bind(target)
      : function () { return null; };

    if (tagName === 'LINK') {
      var rel = String(getAttribute('rel') || '').toLowerCase().split(/\s+/);
      if (rel.some(function (value) {
        return value === 'preload' || value === 'prefetch' || value === 'preconnect' || value === 'modulepreload';
      })) return false;
    }

    if (tagName === 'IMG') {
      var declaredSource = String(getAttribute('src') || '').trim();
      var declaredSourceSet = String(getAttribute('srcset') || '').trim();
      if (!declaredSource && !declaredSourceSet) return false;
    }

    var resourceUrl = target.currentSrc || target.src || target.href || '';
    if (tagName === 'IMG' && pageUrl && sanitizeUrl(String(resourceUrl || '')) === sanitizeUrl(String(pageUrl))) {
      return false;
    }
    return isActionableResourceUrl(String(resourceUrl || ''));
  }

  function sanitizeValue(value, key, depth) {
    var currentDepth = depth || 0;
    if (currentDepth > 8) return '[truncated]';
    if (value === null || typeof value === 'undefined') return value;

    if (typeof value === 'string') {
      return URL_KEY_PATTERN.test(key || '') ? sanitizeUrl(value) : redactText(value);
    }

    if (Array.isArray(value)) {
      return value.slice(0, 100).map(function (item) {
        return sanitizeValue(item, key, currentDepth + 1);
      });
    }

    if (typeof value === 'object') {
      var clean = {};
      Object.keys(value).slice(0, 200).forEach(function (childKey) {
        var normalizedKey = childKey.replace(/[-.]/g, '_');
        if (
          currentDepth === 0 &&
          key === 'properties' &&
          childKey === 'token' &&
          state.config &&
          value[childKey] === state.config.projectToken
        ) {
          clean[childKey] = value[childKey];
          return;
        }
        // Numbers under "value" keys are measurements: price x quantity, and every
        // $web_vitals_*_value (LCP/INP/CLS/FCP), which this rule used to delete so
        // PostHog's web-vitals reports stayed empty. The value rule exists for
        // form and element values, which are strings, and still applies to them.
        if (
          typeof value[childKey] === 'number' &&
          isFinite(value[childKey]) &&
          !SENSITIVE_NUMBER_KEY_PATTERN.test(normalizedKey)
        ) {
          clean[childKey] = value[childKey];
          return;
        }
        if (SENSITIVE_KEY_PATTERN.test(normalizedKey) || /(?:^|_)customer_id(?:$|_)/i.test(normalizedKey)) return;
        clean[childKey] = sanitizeValue(value[childKey], childKey, currentDepth + 1);
      });
      return clean;
    }

    return value;
  }

  function isEmptySyntheticException(event) {
    if (!event || event.event !== '$exception' || !event.properties) return false;

    var properties = event.properties;
    var exceptionList = Array.isArray(properties.$exception_list) ? properties.$exception_list : [];
    if (!exceptionList.length) return false;

    var values = Array.isArray(properties.$exception_values) ? properties.$exception_values.slice() : [];
    exceptionList.forEach(function (exception) {
      if (exception && typeof exception.value !== 'undefined') values.push(exception.value);
    });
    if (values.some(function (value) { return String(value || '').trim() !== ''; })) return false;

    var sourceKeys = ['$exception_sources', '$exception_functions', '$exception_filenames'];
    var hasSource = sourceKeys.some(function (key) {
      var value = properties[key];
      return Array.isArray(value)
        ? value.some(function (item) { return String(item || '').trim() !== ''; })
        : String(value || '').trim() !== '';
    });
    if (hasSource) return false;

    return exceptionList.every(function (exception) {
      if (!exception) return true;
      var frames = exception.stacktrace && Array.isArray(exception.stacktrace.frames)
        ? exception.stacktrace.frames
        : [];
      if (
        frames.length ||
        exception.source ||
        exception.module ||
        exception.filename ||
        exception.function ||
        exception.abs_path
      ) return false;
      return true;
    });
  }

  function isInjectedBridgeException(event) {
    if (!event || event.event !== '$exception' || !event.properties) return false;

    var properties = event.properties;
    if (properties.$exception_fingerprint !== INJECTED_BRIDGE_EXCEPTION_FINGERPRINT) return false;

    var values = Array.isArray(properties.$exception_values) ? properties.$exception_values : [];
    var exceptionList = Array.isArray(properties.$exception_list) ? properties.$exception_list : [];
    if (!values.length || !exceptionList.length) return false;
    if (values.some(function (value) { return String(value || '').trim() !== ''; })) return false;

    return exceptionList.every(function (exception) {
      return exception && exception.type === 'Error';
    });
  }

  function isOpaqueScriptException(event) {
    if (!event || event.event !== '$exception' || !event.properties) return false;
    var properties = event.properties;
    var exceptions = properties.$exception_list;
    if (!Array.isArray(exceptions) || !exceptions.length) return false;
    if (!exceptions.every(function (exception) {
      return exception && exception.type === 'Error' &&
        typeof exception.value === 'string' && /^script error\.?$/i.test(exception.value.trim());
    })) return false;
    if (properties.$exception_values != null && !Array.isArray(properties.$exception_values)) return false;
    var values = properties.$exception_values || [];
    if (values.some(function (value) {
      return typeof value !== 'string' || !/^script error\.?$/i.test(value.trim());
    })) return false;

    // Reuse the source/stack guard. A useful frame or source always stays in Error Tracking.
    return isEmptySyntheticException({
      event: '$exception',
      properties: Object.assign({}, properties, {
        $exception_values: [],
        $exception_list: exceptions.map(function (exception) {
          return Object.assign({}, exception, { value: '' });
        })
      })
    });
  }

  function sanitizeExceptionMessage(value) {
    var message = value.replace(/(?:https?:\/\/|\/\/|\/)[^\s<>"']+/gi, function (url) {
      return url.charAt(0) === '/' && url.charAt(1) !== '/'
        ? url.split('?')[0].split('#')[0]
        : sanitizeUrl(url);
    });
    var fieldPattern = /["']?([\w.-]+)["']?\s*[:=]\s*/g;
    var match;
    while ((match = fieldPattern.exec(message))) {
      var normalizedKey = match[1].replace(/([a-z0-9])([A-Z])/g, '$1_$2').replace(/[-.]/g, '_');
      if (SENSITIVE_KEY_PATTERN.test(normalizedKey) || /(?:^|_)(?:key|api_?key|access_?token|refresh_?token|customer_?id|first_?name|last_?name)(?:$|_)/i.test(normalizedKey)) {
        // Error payloads can contain objects, unquoted addresses or escaped quotes.
        // Keep the diagnostic prefix and stack, never guess where private data ends.
        message = message.slice(0, match.index + match[0].length) + '[redacted]';
        break;
      }
    }
    return redactText(message);
  }

  function isInfiniteScrollClickNoise(event) {
    if (!event || (event.event !== '$rageclick' && event.event !== '$dead_click') || !event.properties) return false;
    var properties = event.properties;
    var haystack = String(properties.$elements_chain || '');
    try {
      haystack += ' ' + JSON.stringify(properties.$elements || []);
    } catch (error) {}
    return /ias-trigger/i.test(haystack);
  }

  function sanitizeEvent(event) {
    if (!event || typeof event !== 'object') return event;

    if (event.event === '$snapshot') return event;
    if (isInjectedBridgeException(event)) return null;
    if (isEmptySyntheticException(event)) return null;
    if (isInfiniteScrollClickNoise(event)) return null;

    var clean = {};
    Object.keys(event).forEach(function (key) {
      clean[key] = key === 'properties'
        ? sanitizeValue(event[key], 'properties', 0)
        : event[key];
    });

    if (event.event === '$exception' && event.properties && clean.properties) {
      // PostHog's diagnostic message is named "value" too. Restore only this exact
      // schema field, with redaction; form/input values must remain stripped.
      if (Array.isArray(clean.properties.$exception_list)) {
        clean.properties.$exception_list.forEach(function (exception, index) {
          var original = event.properties.$exception_list[index];
          if (exception && typeof exception === 'object' && original && typeof original.value === 'string') {
            exception.value = sanitizeExceptionMessage(original.value);
          }
        });
      }
      if (Array.isArray(clean.properties.$exception_values)) {
        clean.properties.$exception_values = event.properties.$exception_values.slice(0, 100).map(function (value) {
          return typeof value === 'string' ? sanitizeExceptionMessage(value) : '[redacted]';
        });
      }
    }
    if (isOpaqueScriptException(event)) {
      // Keep every occurrence for impact analysis; no source is known, so do not
      // count it as an actionable storefront exception or declare it resolved.
      clean.event = 'DG Opaque Script Error';
      Object.keys(clean.properties).forEach(function (key) {
        if (key.indexOf('$exception_') === 0) delete clean.properties[key];
      });
      clean.properties.error_message = 'Script error.';
      clean.properties.error_category = 'opaque_cross_origin';
      clean.properties.diagnostic_version = '2026-09-04';
    }
    return clean;
  }

  function beforeSend(event) {
    var clean = sanitizeEvent(event);
    if (clean && clean.event === 'DG Opaque Script Error') {
      // Cross-origin scripts repeat "Script error." ~14 times a page with no detail.
      // Keep the first per page; the exit summary carries the page total.
      state.opaqueScriptErrors += 1;
      if (state.opaqueScriptErrors > 1) return null;
    }
    return clean;
  }

  function safeStorageGet(storage, key) {
    try {
      return storage ? storage.getItem(key) : null;
    } catch (error) {
      return null;
    }
  }

  function safeStorageSet(storage, key, value) {
    try {
      if (storage) storage.setItem(key, value);
    } catch (error) {}
  }

  function safeStorageRemove(storage, key) {
    try {
      if (storage) storage.removeItem(key);
    } catch (error) {}
  }

  function readNewsletterJourney(rootLike, now) {
    var storage = rootLike && rootLike.sessionStorage;
    var journey = safeJsonParse(safeStorageGet(storage, NEWSLETTER_FUNNEL_KEY) || '', null);
    var startedAt = Number(journey && journey.started_at);
    var age = Number(now) - startedAt;

    if (
      !journey ||
      journey.flow !== 'family12' ||
      !Number.isFinite(startedAt) ||
      age < -60000 ||
      age > NEWSLETTER_FUNNEL_MAX_AGE_MS
    ) {
      safeStorageRemove(storage, NEWSLETTER_FUNNEL_KEY);
      return null;
    }

    return {
      flow: 'family12',
      started_at: startedAt,
      captcha_shown: journey.captcha_shown === true
    };
  }

  function writeNewsletterJourney(rootLike, journey) {
    safeStorageSet(
      rootLike && rootLike.sessionStorage,
      NEWSLETTER_FUNNEL_KEY,
      JSON.stringify(journey)
    );
  }

  function isFamily12NewsletterForm(form) {
    if (!form || typeof form.matches !== 'function' || !form.matches('form.newsletter-form')) return false;
    if (typeof form.querySelector !== 'function') return false;
    var returnTo = form.querySelector('input[name="return_to"]');
    return Boolean(returnTo && returnTo.value === '/pages/family12#Family12Success');
  }

  function trackNewsletterStart(rootLike, form, captureEvent, nowValue) {
    if (!isFamily12NewsletterForm(form)) return false;

    var now = Number.isFinite(Number(nowValue)) ? Number(nowValue) : Date.now();
    var existing = readNewsletterJourney(rootLike, now);
    if (existing && now - existing.started_at < NEWSLETTER_RESUBMIT_DEDUPE_MS) return false;

    writeNewsletterJourney(rootLike, {
      flow: 'family12',
      started_at: now,
      captcha_shown: false
    });

    if (typeof captureEvent === 'function') {
      captureEvent('DG Newsletter Signup Started', { newsletter_flow: 'family12' });
    }
    return true;
  }

  function trackNewsletterPage(rootLike, documentLike, captureEvent, nowValue) {
    var now = Number.isFinite(Number(nowValue)) ? Number(nowValue) : Date.now();
    var journey = readNewsletterJourney(rootLike, now);
    if (!journey || typeof captureEvent !== 'function') return false;

    var pathname = String(rootLike && rootLike.location && rootLike.location.pathname || '');
    if (pathname === '/challenge') {
      if (journey.captcha_shown) return false;
      if (captureEvent('DG Newsletter CAPTCHA Shown', { newsletter_flow: 'family12' }) === false) return false;
      journey.captcha_shown = true;
      writeNewsletterJourney(rootLike, journey);
      return 'captcha';
    }

    var completed = documentLike && typeof documentLike.querySelector === 'function'
      ? documentLike.querySelector('[data-family12-success]')
      : null;
    if (!completed) return false;
    if (captureEvent('DG Newsletter Signup Completed', {
      newsletter_flow: 'family12',
      captcha_shown: journey.captcha_shown
    }) === false) return false;

    safeStorageRemove(rootLike && rootLike.sessionStorage, NEWSLETTER_FUNNEL_KEY);
    return 'completed';
  }

  function cookieValue(cookieString, key) {
    if (!cookieString || !key) return null;
    var prefix = String(key) + '=';
    var parts = String(cookieString).split(';');

    for (var index = 0; index < parts.length; index += 1) {
      var part = parts[index].trim();
      if (part.indexOf(prefix) !== 0) continue;
      try {
        return decodeURIComponent(part.slice(prefix.length));
      } catch (error) {
        return part.slice(prefix.length);
      }
    }

    return null;
  }

  function recentPurchaseTimestamp(storageValue, cookieString, nowMs) {
    var candidates = [storageValue, cookieValue(cookieString, LAST_PURCHASE_KEY)];
    var currentTime = typeof nowMs === 'number' ? nowMs : Date.now();

    for (var index = 0; index < candidates.length; index += 1) {
      var candidate = candidates[index];
      var parsed = candidate ? Date.parse(candidate) : NaN;
      var elapsed = currentTime - parsed;
      if (Number.isFinite(parsed) && elapsed >= 0 && elapsed < PURCHASE_COOLDOWN_MS) return candidate;
    }

    return null;
  }

  function publishShopifyIdentity(shopifyAnalytics, distinctId) {
    var normalized = typeof distinctId === 'string' ? distinctId.trim() : '';
    if (
      !shopifyAnalytics ||
      typeof shopifyAnalytics.publish !== 'function' ||
      !/^[A-Za-z0-9._:-]{1,200}$/.test(normalized)
    ) {
      return false;
    }

    try {
      var result = shopifyAnalytics.publish('deco_garden:posthog_identity', {
        posthog_distinct_id: normalized
      });
      if (result && typeof result.catch === 'function') result.catch(function () {});
      return true;
    } catch (error) {
      return false;
    }
  }

  function scheduleShopifyIdentityReplay(rootLike, distinctId) {
    if (!rootLike || typeof rootLike.setTimeout !== 'function') return false;

    [1500, 4000].forEach(function (delay) {
      rootLike.setTimeout(function () {
        var replayDistinctId = distinctId;
        try {
          if (rootLike.posthog && typeof rootLike.posthog.get_distinct_id === 'function') {
            replayDistinctId = rootLike.posthog.get_distinct_id() || replayDistinctId;
          }
        } catch (error) {}
        publishShopifyIdentity(rootLike.Shopify && rootLike.Shopify.analytics, replayDistinctId);
      }, delay);
    });

    return true;
  }

  // The Customer Events pixel links this browser to Shopify's checkout id by
  // aliasing once per page it receives the identity on. One link per session is
  // enough; publishing on every page cost ~3,500 alias events a day. A page that
  // follows within a few seconds republishes, in case the pixel was not ready yet.
  function shouldPublishIdentity(rootLike, distinctId, nowValue) {
    if (typeof distinctId !== 'string' || !distinctId) return false;
    var storage = rootLike && rootLike.sessionStorage;
    var now = Number.isFinite(Number(nowValue)) ? Number(nowValue) : Date.now();
    var saved = safeJsonParse(safeStorageGet(storage, IDENTITY_PUBLISH_KEY) || '', null);
    var at = saved && saved.id === distinctId ? Number(saved.at) : NaN;
    var age = now - at;
    if (Number.isFinite(age) && age >= IDENTITY_SETTLE_MS && age < IDENTITY_REPUBLISH_MS) return false;
    safeStorageSet(storage, IDENTITY_PUBLISH_KEY, JSON.stringify({
      id: distinctId,
      at: Number.isFinite(age) && age >= 0 && age < IDENTITY_SETTLE_MS ? at : now
    }));
    return true;
  }

  function linkShopifyIdentity(distinctId) {
    if (!shouldPublishIdentity(root, distinctId, Date.now())) return false;
    publishShopifyIdentity(root.Shopify && root.Shopify.analytics, distinctId);
    scheduleShopifyIdentityReplay(root, distinctId);
    return true;
  }

  function readConsent() {
    var privacy = root.Shopify && root.Shopify.customerPrivacy;
    if (!privacy) return { analytics: false, marketing: false };

    try {
      return {
        analytics: privacy.analyticsProcessingAllowed() === true,
        marketing: privacy.marketingAllowed() === true
      };
    } catch (error) {
      return { analytics: false, marketing: false };
    }
  }

  function ensurePrivacyApi(callback, attemptsLeft) {
    var remaining = typeof attemptsLeft === 'number' ? attemptsLeft : 20;

    if (root.Shopify && root.Shopify.customerPrivacy) {
      callback();
      return;
    }

    if (root.Shopify && typeof root.Shopify.loadFeatures === 'function') {
      root.Shopify.loadFeatures(
        [{ name: 'consent-tracking-api', version: '0.1' }],
        function (error) {
          if (!error && root.Shopify.customerPrivacy) callback();
        }
      );
      return;
    }

    if (remaining > 0) {
      root.setTimeout(function () {
        ensurePrivacyApi(callback, remaining - 1);
      }, 200);
    }
  }

  function installPostHogStub() {
    var existing = root.posthog || [];
    if (existing.__SV) return existing;

    var posthog = existing;
    posthog._i = posthog._i || [];
    posthog.init = function (token, options, name) {
      var instanceName = name || 'posthog';
      var instance = instanceName === 'posthog' ? posthog : (posthog[instanceName] = []);
      var methods = [
        'capture', 'identify', 'alias', 'group', 'register', 'register_once', 'unregister',
        'reset', 'opt_in_capturing', 'opt_out_capturing', 'has_opted_out_capturing',
        'startSessionRecording', 'stopSessionRecording', 'get_distinct_id', 'get_session_id',
        'set_config', 'debug', 'onFeatureFlags', 'getFeatureFlag', 'reloadFeatureFlags'
      ];

      instance.people = instance.people || [];
      ['set', 'set_once'].forEach(function (method) {
        instance.people[method] = function () {
          instance.people.push([method].concat(Array.prototype.slice.call(arguments)));
        };
      });

      methods.forEach(function (method) {
        instance[method] = function () {
          instance.push([method].concat(Array.prototype.slice.call(arguments)));
        };
      });

      posthog._i.push([token, options, instanceName]);

      if (!documentRef.querySelector('script[data-dg-posthog-sdk]')) {
        var script = documentRef.createElement('script');
        var firstScript = documentRef.getElementsByTagName('script')[0];
        script.async = true;
        script.crossOrigin = 'anonymous';
        script.dataset.dgPosthogSdk = 'true';
        script.src = options.api_host + '/static/array.js';
        firstScript.parentNode.insertBefore(script, firstScript);
      }

      return instance;
    };
    posthog.__SV = 1;
    root.posthog = posthog;
    return posthog;
  }

  function campaignProperties() {
    var output = {};
    if (!root.location || typeof URLSearchParams === 'undefined') return output;
    var params = new URLSearchParams(root.location.search || '');
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(function (key) {
      var value = params.get(key);
      if (value) output['dg_' + key] = redactText(value).slice(0, 120);
    });
    return output;
  }

  function visitorProperties() {
    return {
      dg_store: 'Deco Garden',
      dg_page_type: state.config.pageType || 'unknown',
      dg_template: state.config.template || 'unknown',
      dg_locale: state.config.locale || 'he',
      dg_currency: state.config.currency || 'ILS',
      dg_customer_status: state.config.customerId ? 'logged_in' : 'guest',
      dg_visitor_type: state.visitorType,
      dg_privacy_version: '2026-08-24'
    };
  }

  function markVisitorType() {
    var firstSeen = safeStorageGet(root.localStorage, FIRST_SEEN_KEY);
    if (firstSeen) {
      state.visitorType = 'returning';
      return;
    }

    state.visitorType = 'new';
    safeStorageSet(root.localStorage, FIRST_SEEN_KEY, new Date().toISOString());
  }

  function hasRecentPurchase() {
    var storedTimestamp = safeStorageGet(root.localStorage, LAST_PURCHASE_KEY);
    var timestamp = recentPurchaseTimestamp(
      storedTimestamp,
      documentRef && documentRef.cookie ? documentRef.cookie : '',
      Date.now()
    );
    if (timestamp) {
      if (timestamp !== storedTimestamp) safeStorageSet(root.localStorage, LAST_PURCHASE_KEY, timestamp);
      try {
        documentRef.cookie = LAST_PURCHASE_KEY + '=' + encodeURIComponent(timestamp) +
          '; path=/; max-age=' + Math.floor(PURCHASE_COOLDOWN_MS / 1000) + '; SameSite=Lax; Secure';
      } catch (error) {}
    }
    return Boolean(timestamp);
  }

  function readSessionState() {
    var saved = safeJsonParse(safeStorageGet(root.sessionStorage, SESSION_KEY) || '{}', {});
    var lastUpdatedAt = Number(saved.lastUpdatedAt || 0);
    if (!lastUpdatedAt || Date.now() - lastUpdatedAt > SESSION_MAX_IDLE_MS) return;

    state.activeSeconds = Number(saved.activeSeconds || 0);
    state.meaningfulClicks = Number(saved.meaningfulClicks || 0);
    state.productViews = Number(saved.productViews || 0);
    state.searches = Number(saved.searches || 0);
    state.addToCart = Number(saved.addToCart || 0);
    // Sessions saved before 2026-09-25 named the same click counter checkoutStarts.
    state.checkoutClicks = Number(saved.checkoutClicks || saved.checkoutStarts || 0);
    state.pageviews = Number(saved.pageviews || 0);
    state.maxScrollDepth = Number(saved.maxScrollDepth || 0);
    state.highIntentFired = saved.highIntentFired === true || safeStorageGet(root.sessionStorage, HIGH_INTENT_KEY) === '1';
    state.metaHighIntentSent = saved.metaHighIntentSent === true;
    state.highIntentEventId = saved.highIntentEventId || null;
    state.highIntentReason = saved.highIntentReason || null;
  }

  function persistSessionState() {
    if (!state.analyticsAllowed) return;
    state.lastUpdatedAt = Date.now();
    safeStorageSet(root.sessionStorage, SESSION_KEY, JSON.stringify({
      activeSeconds: state.activeSeconds,
      meaningfulClicks: state.meaningfulClicks,
      productViews: state.productViews,
      searches: state.searches,
      addToCart: state.addToCart,
      checkoutClicks: state.checkoutClicks,
      pageviews: state.pageviews,
      maxScrollDepth: state.maxScrollDepth,
      highIntentFired: state.highIntentFired,
      metaHighIntentSent: state.metaHighIntentSent,
      highIntentEventId: state.highIntentEventId,
      highIntentReason: state.highIntentReason,
      lastUpdatedAt: state.lastUpdatedAt
    }));
  }

  function writeDistinctIdBridge() {
    if (!state.analyticsAllowed || !root.posthog || typeof root.posthog.get_distinct_id !== 'function') return null;
    var distinctId = root.posthog.get_distinct_id();
    if (!distinctId || typeof distinctId !== 'string') return null;

    safeStorageSet(root.localStorage, DISTINCT_ID_KEY, distinctId);
    try {
      documentRef.cookie = DISTINCT_ID_KEY + '=' + encodeURIComponent(distinctId) + '; path=/; max-age=31536000; SameSite=Lax; Secure';
    } catch (error) {}
    return distinctId;
  }

  function clearDistinctIdBridge() {
    safeStorageRemove(root.localStorage, DISTINCT_ID_KEY);
    try {
      documentRef.cookie = DISTINCT_ID_KEY + '=; path=/; max-age=0; SameSite=Lax; Secure';
    } catch (error) {}
  }

  function clearPurchaseBridge() {
    safeStorageRemove(root.localStorage, LAST_PURCHASE_KEY);
    try {
      documentRef.cookie = LAST_PURCHASE_KEY + '=; path=/; max-age=0; SameSite=Lax; Secure';
    } catch (error) {}
  }

  function identifyCustomer() {
    var currentId = state.config.customerId ? String(state.config.customerId) : null;
    var previousId = safeStorageGet(root.localStorage, CUSTOMER_KEY);

    if (!currentId && previousId) {
      root.posthog.reset();
      safeStorageRemove(root.localStorage, CUSTOMER_KEY);
      writeDistinctIdBridge();
      return;
    }

    if (!currentId) return;

    if (previousId && previousId !== currentId) root.posthog.reset();
    root.posthog.identify('shopify_customer_' + currentId, {
      dg_customer_status: 'logged_in',
      dg_locale: state.config.locale || 'he'
    });
    safeStorageSet(root.localStorage, CUSTOMER_KEY, currentId);
    writeDistinctIdBridge();
  }

  function baseEventProperties() {
    return {
      dg_page_type: state.config.pageType || 'unknown',
      dg_template: state.config.template || 'unknown',
      dg_customer_status: state.config.customerId ? 'logged_in' : 'guest',
      dg_visitor_type: state.visitorType,
      dg_marketing_consent: state.marketingAllowed,
      $process_person_profile: false
    };
  }

  function mirrorToClarity(rootLike, name, properties) {
    var build = CLARITY_MIRROR[name];
    if (!build || !rootLike || typeof rootLike.clarity !== 'function') return false;
    try {
      var spec = build(properties || {}) || {};
      var tags = spec.tags || {};
      Object.keys(tags).forEach(function (key) {
        var tagValue = tags[key];
        if (Array.isArray(tagValue)) {
          tagValue = tagValue.filter(function (item) { return item !== null && item !== undefined && item !== ''; })
            .slice(0, 10)
            .map(function (item) { return String(item).slice(0, 60); });
          if (!tagValue.length) return;
        } else {
          if (tagValue === null || tagValue === undefined || tagValue === '') return;
          tagValue = String(tagValue).slice(0, 80);
        }
        rootLike.clarity('set', key, tagValue);
      });
      if (spec.event) rootLike.clarity('event', spec.event);
      return true;
    } catch (error) {
      return false;
    }
  }

  function linkClaritySession(rootLike) {
    // Lets a PostHog session be opened as the matching Clarity recording
    // (Clarity filter: custom tag dg_ph_session). Technical id only.
    if (!rootLike || typeof rootLike.clarity !== 'function' || !rootLike.posthog) return false;
    try {
      var sessionId = typeof rootLike.posthog.get_session_id === 'function' ? rootLike.posthog.get_session_id() : null;
      if (typeof sessionId !== 'string' || !/^[A-Za-z0-9-]{8,80}$/.test(sessionId)) return false;
      rootLike.clarity('set', 'dg_ph_session', sessionId);
      return true;
    } catch (error) {
      return false;
    }
  }

  function capture(name, properties, options) {
    if (!state.initialized || !state.analyticsAllowed || !root.posthog || typeof root.posthog.capture !== 'function') return false;
    var payload = Object.assign(baseEventProperties(), properties || {});
    if (options) {
      root.posthog.capture(name, payload, options);
    } else {
      root.posthog.capture(name, payload);
    }
    mirrorToClarity(root, name, payload);
    return true;
  }

  function deliveryCostBucket(value) {
    var cost = Number(value);
    if (!Number.isFinite(cost) || cost < 0) return 'unknown';
    if (cost === 0) return 'free';
    if (cost <= 39) return '1_39';
    if (cost <= 99) return '40_99';
    if (cost <= 199) return '100_199';
    return '200_plus';
  }

  function deliveryQuoteProperties(quote, context) {
    var source = context && context.source || quote && quote.quoteSource || 'unknown';
    var baseCost = Math.max(0, Number(quote && quote.deliveryCost) || 0);
    var freeAbove = Math.max(0, Number(quote && quote.freeDeliveryAbove) || 0);
    var smallFee = Math.max(0, Number(quote && quote.smallOrderFee) || 0);
    var smallThreshold = Math.max(0, Number(quote && quote.smallOrderThreshold) || 0);
    var minimumOrder = Math.max(0, Number(quote && (quote.minOrderAmount || quote.minOrderBigSku)) || 0);
    var rawCartTotal = quote && quote.cartTotal;
    var hasCartTotal = rawCartTotal !== null && rawCartTotal !== '' && Number.isFinite(Number(rawCartTotal));
    var cartTotal = hasCartTotal ? Math.max(0, Number(rawCartTotal)) : null;
    var hasBigSku = quote && typeof quote.hasBigSku === 'boolean' ? quote.hasBigSku : false;
    var allowed = !!(quote && quote.allowed);
    var effectiveCost = baseCost;

    if (allowed && hasCartTotal) {
      if (freeAbove > 0 && cartTotal >= freeAbove) {
        effectiveCost = 0;
      } else if (baseCost > 0 && smallFee > 0 && smallThreshold > 0 && cartTotal < smallThreshold) {
        effectiveCost = smallFee;
      }
    }

    var status = allowed ? 'available' : 'unavailable';
    if (allowed && hasBigSku && hasCartTotal && minimumOrder > 0 && cartTotal < minimumOrder) {
      status = 'heavy_minimum_blocked';
    }

    var properties = {
      delivery_zone: redactText(String(quote && quote.zoneName || 'unknown')).slice(0, 80),
      delivery_quote_source: redactText(String(source)).slice(0, 40),
      delivery_status: status,
      delivery_reason: redactText(String(quote && quote.reason || 'unknown')).slice(0, 40),
      base_delivery_cost: baseCost,
      effective_delivery_cost: effectiveCost,
      delivery_cost_bucket: deliveryCostBucket(effectiveCost),
      free_delivery_above: freeAbove,
      amount_to_free_delivery: hasCartTotal && freeAbove > 0 ? Math.max(0, freeAbove - cartTotal) : undefined,
      minimum_order_amount: minimumOrder,
      small_order_fee: smallFee,
      small_order_threshold: smallThreshold,
      cart_total: hasCartTotal ? cartTotal : undefined,
      item_count: quote && Number.isFinite(Number(quote.itemCount)) ? Math.max(0, Number(quote.itemCount)) : undefined,
      has_big_sku: hasBigSku,
      high_intent_at_quote: !!(context && context.highIntent)
    };

    Object.keys(properties).forEach(function (key) {
      if (typeof properties[key] === 'undefined') delete properties[key];
    });
    return properties;
  }

  function handleDeliveryQuote(event) {
    if (!state.analyticsAllowed || !state.initialized) return;
    var quote = event && event.detail;
    if (!quote || typeof quote !== 'object') return;

    var properties = deliveryQuoteProperties(quote, {
      source: quote.quoteSource || state.config && state.config.pageType || 'unknown',
      highIntent: state.highIntentFired
    });
    var quoteKey = JSON.stringify(properties);
    if (safeStorageGet(root.sessionStorage, DELIVERY_QUOTE_KEY) === quoteKey) return;
    safeStorageSet(root.sessionStorage, DELIVERY_QUOTE_KEY, quoteKey);
    capture('DG Delivery Quote Viewed', properties);
  }

  function productProperties(product) {
    if (!product) return {};
    return {
      product_id: product.product_id ? String(product.product_id) : undefined,
      variant_id: product.variant_id ? String(product.variant_id) : undefined,
      sku: product.sku || undefined,
      category: product.category || undefined,
      name: redactText(product.name || ''),
      brand: redactText(product.brand || ''),
      variant: redactText(product.variant || ''),
      price: typeof product.price === 'number' ? product.price : undefined,
      quantity: Number(product.quantity || 1),
      currency: product.currency || state.config.currency || 'ILS',
      value: typeof product.price === 'number' ? product.price * Number(product.quantity || 1) : undefined,
      url: product.url ? sanitizeUrl(product.url) : undefined
    };
  }

  function sendInitialEvents() {
    if (state.initialEventsSent) return;
    state.initialEventsSent = true;
    state.pageviews += 1;

    if (state.config.product) {
      state.productViews += 1;
      capture('Product Viewed', productProperties(state.config.product));
    }

    if (state.config.collection) {
      capture('Product List Viewed', {
        list_id: String(state.config.collection.collection_id || state.config.collection.handle || ''),
        category: redactText(state.config.collection.name || ''),
        url: sanitizeUrl(state.config.collection.url || root.location.href)
      });
    }

    if (state.config.pageType === 'search') {
      var query = new URLSearchParams(root.location.search || '').get('q');
      if (query) {
        state.searches += 1;
        capture('Products Searched', { query: redactText(query).slice(0, 100) });
      }
    }

    if (state.config.pageType === 'cart') {
      capture('Cart Viewed', {
        total: Number(state.config.cart && state.config.cart.total || 0),
        currency: state.config.currency || 'ILS',
        item_count: Number(state.config.cart && state.config.cart.item_count || 0)
      });
    }

    persistSessionState();
    evaluateHighIntent();
  }

  function calculateIntentScore(metrics) {
    var score = 0;
    score += Math.min(30, Math.floor(Number(metrics.activeSeconds || 0) / 3));
    score += Math.min(28, Number(metrics.meaningfulClicks || 0) * 4);
    score += Math.min(18, Number(metrics.productViews || 0) * 6);
    score += Math.min(20, Number(metrics.addToCart || 0) * 20);
    score += Math.min(12, Number(metrics.searches || 0) * 6);
    if (Number(metrics.maxScrollDepth || 0) >= 75) score += 5;
    if (Number(metrics.checkoutClicks || metrics.checkoutStarts || 0) > 0) score += 20;
    return Math.min(100, score);
  }

  function highIntentReason(metrics) {
    if (metrics.purchasedRecently) return null;
    if (metrics.addToCart >= 1 && metrics.activeSeconds >= 45 && metrics.meaningfulClicks >= 3) return 'cart_engaged';
    if (metrics.productViews >= 3 && metrics.activeSeconds >= 60 && metrics.meaningfulClicks >= 4) return 'multi_product_research';
    if (metrics.searches >= 2 && metrics.activeSeconds >= 60 && metrics.meaningfulClicks >= 4) return 'repeat_search';
    if (metrics.activeSeconds >= 90 && metrics.meaningfulClicks >= 6) return 'long_active_clicking';
    return null;
  }

  function engagementProperties(reason) {
    return {
      active_seconds: state.activeSeconds,
      meaningful_clicks: state.meaningfulClicks,
      product_views: state.productViews,
      searches: state.searches,
      add_to_cart_count: state.addToCart,
      checkout_clicks: state.checkoutClicks,
      pageviews: state.pageviews,
      max_scroll_depth: state.maxScrollDepth,
      intent_score: calculateIntentScore(state),
      intent_reason: reason || undefined,
      purchased_last_30_days: state.purchasedRecently,
      meta_event_name: 'DGHighIntent',
      meta_event_id: state.highIntentEventId || undefined
    };
  }

  function makeEventId() {
    if (root.crypto && typeof root.crypto.randomUUID === 'function') return root.crypto.randomUUID();
    return 'dg-hi-' + Date.now() + '-' + Math.random().toString(36).slice(2, 12);
  }

  function sendMetaHighIntent(attemptsLeft) {
    var remaining = typeof attemptsLeft === 'number' ? attemptsLeft : 20;
    if (state.metaHighIntentSent || !state.marketingAllowed || state.purchasedRecently || !state.highIntentFired) return;

    if (typeof root.fbq === 'function') {
      root.fbq(
        'trackCustom',
        'DGHighIntent',
        {
          intent_score: calculateIntentScore(state),
          active_seconds: state.activeSeconds,
          meaningful_clicks: state.meaningfulClicks,
          product_views: state.productViews,
          add_to_cart_count: state.addToCart,
          // Lets Meta audiences keep only the strongest signal, e.g. DGHighIntent
          // where intent_reason = cart_engaged (buyers most often) — see docs.
          intent_reason: state.highIntentReason || undefined,
          currency: state.config.currency || 'ILS'
        },
        { eventID: state.highIntentEventId }
      );
      state.metaHighIntentSent = true;
      persistSessionState();
      return;
    }

    if (remaining > 0) {
      root.setTimeout(function () {
        sendMetaHighIntent(remaining - 1);
      }, 500);
    }
  }

  function evaluateHighIntent() {
    if (state.highIntentFired) {
      sendMetaHighIntent();
      return;
    }

    var reason = highIntentReason(state);
    if (!reason) return;

    state.highIntentFired = true;
    state.highIntentEventId = makeEventId();
    state.highIntentReason = reason;
    safeStorageSet(root.sessionStorage, HIGH_INTENT_KEY, '1');
    persistSessionState();
    capture('DG High Intent', engagementProperties(reason));
    sendMetaHighIntent();
  }

  function interactionCategory(element) {
    if (!element) return 'other';
    var tagName = String(element.tagName || '').toUpperCase();
    // Typing into the cart form is engagement, not a checkout attempt. The form's
    // inputs carry "dz-checkout__input" classes, so class names alone would count
    // every tap on a name or phone field as a checkout click.
    if (tagName === 'INPUT' || tagName === 'SELECT' || tagName === 'TEXTAREA') return 'form_field';
    var text = String(element.textContent || element.getAttribute('aria-label') || '').toLowerCase();
    var href = String(element.getAttribute('href') || '').toLowerCase();
    var classes = String(element.className || '').toLowerCase();
    var joined = [text, href, classes, element.id || ''].join(' ');

    if (/cart\/add|add.to.cart|הוספה לסל|הוסף לסל|product-form__submit/.test(joined)) return 'add_to_cart';
    if (
      /\/checkout/.test(href) ||
      element.getAttribute('name') === 'checkout' ||
      element.id === 'dz-pay-btn' ||
      /checkout|לתשלום|קופה/.test(text)
    ) return 'checkout';
    if (/\/products\/|product-card|card__heading/.test(joined)) return 'product';
    if (/search|חיפוש|חפש/.test(joined)) return 'search';
    if (/filter|facet|סינון/.test(joined)) return 'filter';
    if (/whatsapp|tel:|mailto:|צור קשר/.test(joined)) return 'contact';
    if (element.tagName === 'A' || /menu|nav|header|footer/.test(joined)) return 'navigation';
    return 'other';
  }

  function interactionLabel(element) {
    if (!element) return '';
    var getAttribute = typeof element.getAttribute === 'function'
      ? element.getAttribute.bind(element)
      : function () { return null; };
    var candidates = [
      getAttribute('data-dg-analytics-label'),
      getAttribute('aria-label'),
      getAttribute('title'),
      typeof element.innerText === 'string' ? element.innerText : element.textContent,
      getAttribute('href')
    ];

    for (var index = 0; index < candidates.length; index += 1) {
      var candidate = String(candidates[index] || '').replace(/\s+/g, ' ').trim();
      if (candidate) return redactText(candidate).slice(0, 160);
    }
    return String(element.tagName || 'unknown').toLowerCase();
  }

  function destinationPath(element) {
    if (!element || typeof element.getAttribute !== 'function') return undefined;
    var href = element.getAttribute('href');
    if (!href) return undefined;
    try {
      return new URL(href, root.location && root.location.origin ? root.location.origin : 'https://decogarden.co.il').pathname;
    } catch (error) {
      return String(href).split('?')[0].split('#')[0].slice(0, 300);
    }
  }

  function productFromLink(element) {
    var href = element && element.getAttribute ? element.getAttribute('href') : '';
    if (!href || href.indexOf('/products/') === -1) return null;
    var card = element.closest ? element.closest('[data-product-id], .card-wrapper, .product-card-wrapper') : null;
    var match = href.match(/\/products\/([^?#/]+)/);
    return {
      product_id: card && card.getAttribute('data-product-id') || undefined,
      name: redactText(
        card && card.querySelector('.card__heading, .full-unstyled-link')
          ? card.querySelector('.card__heading, .full-unstyled-link').textContent.trim()
          : element.textContent.trim()
      ),
      handle: match ? match[1] : undefined,
      url: sanitizeUrl(href)
    };
  }

  // PostHog's own rage-click detection lives inside autocapture, which is off to
  // stay inside the monthly event quota (DG Meaningful Click already records every
  // useful click). Same definition: 3 clicks within 1 second within 30px.
  function handleRageClick(event) {
    if (!state.analyticsAllowed || !event || typeof event.clientX !== 'number' || event.button > 0) return;
    var now = Date.now();
    var x = event.clientX;
    var y = event.clientY;
    state.rageClicks = state.rageClicks.filter(function (click) { return now - click.t < 1000; });
    state.rageClicks.push({ x: x, y: y, t: now });
    var near = state.rageClicks.filter(function (click) {
      return Math.abs(click.x - x) <= 30 && Math.abs(click.y - y) <= 30;
    });
    if (near.length < 3 || now - state.lastRageClickAt < 2000) return;
    state.lastRageClickAt = now;
    state.rageClicks = [];
    var element = event.target && event.target.closest
      ? event.target.closest('a, button, summary, label, select, input, textarea, [role="button"], img, svg') || event.target
      : event.target;
    if (!element || !element.tagName) return;
    var tagName = String(element.tagName).toLowerCase();
    var interactive = !!(element.closest && element.closest('a, button, summary, label, select, input, textarea, [role="button"]'));
    capture('DG Rage Click', {
      element_type: tagName.slice(0, 40),
      // A field's label never reads its typed value; the tag name is enough.
      interaction_label: /^(?:input|textarea|select)$/.test(tagName) ? tagName : interactionLabel(element),
      interaction_category: interactive ? interactionCategory(element) : 'not_clickable',
      destination_path: destinationPath(element)
    });
  }

  function handleClick(event) {
    if (!state.analyticsAllowed || !event || event.defaultPrevented || event.button > 0) return;
    var target = event.target && event.target.closest
      ? event.target.closest('a, button, summary, input, select, [role="button"], [data-product-url]')
      : null;
    if (!target || target.matches(IGNORED_CLICK_SELECTOR) || target.closest(IGNORED_CLICK_SELECTOR)) return;

    state.lastInteractionAt = Date.now();
    state.meaningfulClicks += 1;
    var category = interactionCategory(target);

    capture('DG Meaningful Click', {
      interaction_category: category,
      interaction_label: interactionLabel(target),
      element_type: String(target.tagName || 'unknown').toLowerCase().slice(0, 40),
      destination_path: destinationPath(target)
    });

    if (category === 'product') {
      var product = productFromLink(target);
      if (product) capture('Product Clicked', product);
    }

    if (category === 'checkout') trackCheckoutClick('click', target);
    persistSessionState();
    evaluateHighIntent();
  }

  function checkoutButtonLocation(element, pageType) {
    try {
      if (element && typeof element.closest === 'function') {
        if (element.closest('cart-drawer, #CartDrawer, .cart-drawer')) return 'cart_drawer';
        if (element.closest('#main-cart-footer, #dz-shipping-form')) return 'cart_page';
      }
    } catch (error) {}
    return String(pageType || 'unknown').slice(0, 40);
  }

  // A storefront click on a "continue to payment" control is an attempt, not a
  // started checkout: the drawer button opens /cart, and the cart page can still
  // block the click (missing details, terms, heavy-item minimum). The real
  // "Checkout Started" comes only from Shopify's checkout through the Customer
  // Events pixel, so checkout funnels count shoppers who actually reached it.
  function trackCheckoutClick(source, element) {
    var now = Date.now();
    if (state.lastCheckoutClickAt && now - state.lastCheckoutClickAt < 3000) return;
    state.lastCheckoutClickAt = now;
    state.checkoutClicks += 1;
    capture('DG Checkout Button Clicked', {
      source: source,
      button_location: checkoutButtonLocation(element, state.config.pageType),
      destination_path: destinationPath(element),
      total: Number(state.config.cart && state.config.cart.total || 0),
      currency: state.config.currency || 'ILS',
      item_count: Number(state.config.cart && state.config.cart.item_count || 0)
    });
    persistSessionState();
    evaluateHighIntent();
  }

  function handleSubmit(event) {
    if (!state.analyticsAllowed || !event || !event.target) return;
    state.lastInteractionAt = Date.now();
    var form = event.target;
    var action = String(form.action || '').toLowerCase();

    trackNewsletterStart(root, form, capture, Date.now());

    if (action.indexOf('/search') !== -1 || form.getAttribute('role') === 'search') {
      var input = form.querySelector('input[type="search"], input[name="q"]');
      var query = input && input.value ? redactText(input.value.trim()).slice(0, 100) : '';
      if (query) {
        state.searches += 1;
        capture('Products Searched', { query: query, source: 'form_submit' });
      }
    }

    if (action.indexOf('/checkout') !== -1) trackCheckoutClick('form_submit', form);
    persistSessionState();
    evaluateHighIntent();
  }

  function normalizeCartItem(item) {
    if (!item || typeof item !== 'object') return null;
    return {
      product_id: item.product_id ? String(item.product_id) : undefined,
      variant_id: item.variant_id || item.id ? String(item.variant_id || item.id) : undefined,
      sku: item.sku || undefined,
      category: item.product_type || undefined,
      name: item.product_title || item.title || '',
      brand: item.vendor || '',
      variant: item.variant_title || '',
      price: typeof item.final_price === 'number'
        ? item.final_price / 100
        : (typeof item.price === 'number' ? item.price / 100 : undefined),
      quantity: Number(item.quantity || 1),
      currency: state.config.currency || 'ILS',
      url: item.url || undefined
    };
  }

  function captureAddedItem(item, source) {
    if (!state.analyticsAllowed) return;
    var normalized = normalizeCartItem(item) || Object.assign({}, state.config.product || {}, { quantity: 1 });
    var dedupeKey = [normalized.product_id, normalized.variant_id, normalized.quantity].join(':');
    var now = Date.now();
    if (state.lastAddCapture && state.lastAddCapture.key === dedupeKey && now - state.lastAddCapture.at < 2500) return;
    state.lastAddCapture = { key: dedupeKey, at: now };
    state.addToCart += 1;
    capture('Product Added', Object.assign(productProperties(normalized), { source: source }));
    persistSessionState();
    evaluateHighIntent();
  }

  function wrapShopifyMetricsRetry(environment, originalFetch, canRetry, report) {
    // A bounded recovery for Shop JS technical metrics, not checkout, commerce
    // events, other Shopify telemetry, or arbitrary third-party requests.
    var endpoint = 'https://otlp-http-production.shopifysvc.com/v1/metrics';
    var retryUsed = false;

    function allowed(options) {
      try {
        return canRetry() === true && environment.navigator.onLine === true &&
          environment.navigator.doNotTrack !== '1' && environment.doNotTrack !== '1' &&
          environment.document.visibilityState === 'visible' &&
          !(options.signal && options.signal.aborted);
      } catch (error) {
        return false;
      }
    }

    function retryOptions(input, options) {
      try {
        if (input !== endpoint || !options || options.method !== 'POST' ||
            typeof options.body !== 'string' || options.body.length > 51200 ||
            typeof environment.Headers !== 'function' ||
            typeof environment.setTimeout !== 'function' || !allowed(options)) return null;
        var payload = JSON.parse(options.body);
        var resources = payload && payload.resourceMetrics;
        if (!Array.isArray(resources) || resources.length !== 1) return null;
        var attributes = resources[0].resource && resources[0].resource.attributes;
        if (!Array.isArray(attributes)) return null;
        var services = attributes.filter(function (attribute) {
          return attribute && attribute.key === 'service.name';
        });
        if (services.length !== 1 || !services[0].value || services[0].value.stringValue !== 'shop-js') return null;
        // Strings and a copied Headers object cannot change during the delay.
        return Object.assign({}, options, { headers: new environment.Headers(options.headers) });
      } catch (error) {
        return null;
      }
    }

    function networkFailure(error) {
      try {
        return error && error.name === 'TypeError' && error.message === 'Failed to fetch';
      } catch (inspectionError) {
        return false;
      }
    }

    function retryFailureType(error) {
      try {
        return error && error.name === 'AbortError' ? 'abort' : 'other';
      } catch (inspectionError) {
        return 'other';
      }
    }

    function responseRecovered(response) {
      try {
        return !!(response && response.ok);
      } catch (inspectionError) {
        return false;
      }
    }

    function responseFailure(response) {
      try {
        var status = response && response.status;
        if (Number.isInteger(status) && status >= 100 && status <= 599) {
          return { type: 'http', status: status };
        }
      } catch (inspectionError) {
        // Hostile or unavailable response metadata is classified without escaping.
      }
      return { type: 'other' };
    }

    function reportOutcome(outcome, failureType, httpStatus) {
      try {
        if (canRetry() === true && typeof report === 'function') {
          var properties = { retry_outcome: outcome };
          if (outcome === 'failed') {
            properties.retry_failure_type = failureType;
            if (failureType === 'http') properties.retry_http_status = httpStatus;
          }
          report(properties);
        }
      } catch (error) {
        // Optional diagnostics must not change a request's result.
      }
    }

    return function () {
      var receiver = this;
      var options = retryUsed ? null : retryOptions(arguments[0], arguments[1]);
      var args = options ? Array.prototype.slice.call(arguments) : null;
      var result = originalFetch.apply(receiver, arguments);
      if (!options) return result;

      return result.catch(function (originalError) {
        if (retryUsed || !networkFailure(originalError) || !allowed(options)) throw originalError;
        retryUsed = true; // At most one additional request per page, including concurrent failures.
        args[1] = options;
        reportOutcome('scheduled');

        return new Promise(function (resolve, reject) {
          environment.setTimeout(function () {
            if (!allowed(options)) {
              reportOutcome('cancelled');
              reject(originalError);
              return;
            }
            Promise.resolve().then(function () {
              // Same endpoint, body, headers, credentials, signal and keepalive.
              // No proxy, fake response, or bypass of a blocked connection.
              return originalFetch.apply(receiver, args);
            }).then(function (response) {
              if (responseRecovered(response)) {
                reportOutcome('recovered');
              } else {
                var failure = responseFailure(response);
                reportOutcome('failed', failure.type, failure.status);
              }
              resolve(response);
            }, function (retryError) {
              var repeatedNetworkFailure = networkFailure(retryError);
              reportOutcome('failed', repeatedNetworkFailure ? 'network' : retryFailureType(retryError));
              // Preserve the original Shop JS stack for recurring network errors.
              // A different programming/abort error must still surface as itself.
              reject(repeatedNetworkFailure ? originalError : retryError);
            });
          }, 500);
        });
      });
    };
  }

  function patchFetch() {
    if (typeof root.fetch !== 'function' || root.fetch.__dgPostHogWrapped) return;
    var originalFetch = wrapShopifyMetricsRetry(root, root.fetch, function () {
      return state.analyticsAllowed;
    }, function (properties) {
      capture('DG Shopify Metrics Retry', properties);
    });

    function wrappedFetch(input, init) {
      var url = typeof input === 'string' ? input : (input && input.url || '');
      var promise = originalFetch.apply(this, arguments);
      if (!/\/cart\/add(?:\.js)?(?:[?#]|$)/.test(url)) return promise;

      return promise.then(function (response) {
        if (!response || !response.ok) return response;
        try {
          response.clone().json().then(function (payload) {
            var items = Array.isArray(payload && payload.items) ? payload.items : [payload];
            items.filter(Boolean).forEach(function (item) {
              captureAddedItem(item, 'cart_api');
            });
          }).catch(function () {
            captureAddedItem(null, 'cart_api');
          });
        } catch (error) {
          captureAddedItem(null, 'cart_api');
        }
        return response;
      });
    }

    wrappedFetch.__dgPostHogWrapped = true;
    wrappedFetch.__dgPostHogOriginal = originalFetch;
    root.fetch = wrappedFetch;
  }

  function scrollDepth() {
    if (!documentRef || !documentRef.documentElement) return 0;
    var top = root.scrollY || documentRef.documentElement.scrollTop || 0;
    var viewport = root.innerHeight || documentRef.documentElement.clientHeight || 0;
    var height = Math.max(documentRef.body ? documentRef.body.scrollHeight : 0, documentRef.documentElement.scrollHeight || 0);
    if (!height) return 0;
    return Math.min(100, Math.round(((top + viewport) / height) * 100));
  }

  function handleScroll() {
    if (!state.analyticsAllowed) return;
    var depth = scrollDepth();
    if (depth <= state.maxScrollDepth) return;
    state.maxScrollDepth = depth;
    [50, 75, 90].forEach(function (threshold) {
      var key = 'dg_ph_scroll_' + threshold;
      if (depth >= threshold && safeStorageGet(root.sessionStorage, key) !== '1') {
        safeStorageSet(root.sessionStorage, key, '1');
        capture('DG Scroll Depth', { depth_percent: threshold });
      }
    });
    persistSessionState();
    evaluateHighIntent();
  }

  // ---- Cart page form: where shoppers stop before Shopify checkout ----------
  // The cart page asks for delivery details, terms and (for heavy items) a
  // minimum before it sends the shopper on. These events record which gate
  // stopped a click and which fields were filled, by field NAME only. The
  // typed values never reach this module.
  var CART_FIELD_KEYS = ['fname', 'lname', 'phone', 'email', 'email_invalid', 'city', 'street', 'apt', 'invoice_name'];
  var CART_BLOCK_REASONS = ['heavy_minimum', 'missing_fields', 'terms_unchecked', 'note_save_failed'];
  var CART_DELIVERY_STATES = ['pending', 'loading', 'price', 'error', 'pickup', 'deferred', 'unverified'];
  var DELIVERY_CHECK_FAILURES = ['address_not_found', 'check_error', 'quote_timeout', 'map_unavailable'];
  var CART_FORM_QUEUE_LIMIT = 30;
  var cartForm = freshCartFormState();
  var pendingCartForm = [];

  function freshCartFormState() {
    return {
      started: false,
      startedAt: 0,
      blocks: 0,
      lastBlockReason: '',
      passed: false,
      abandonReported: false,
      snapshot: null
    };
  }

  function cleanCartFieldKeys(list) {
    if (!Array.isArray(list)) return [];
    var output = [];
    list.forEach(function (item) {
      var key = String(item || '');
      if (CART_FIELD_KEYS.indexOf(key) !== -1 && output.indexOf(key) === -1) output.push(key);
    });
    return output.slice(0, CART_FIELD_KEYS.length);
  }

  function cartDeliveryMode(details) {
    return details && details.mode === 'pickup' ? 'pickup' : 'delivery';
  }

  function cartDeliveryState(value) {
    return CART_DELIVERY_STATES.indexOf(value) !== -1 ? value : 'unknown';
  }

  function eventTime(details) {
    var at = Number(details && details.at);
    return isFinite(at) && at > 0 ? at : Date.now();
  }

  function trackCartForm(kind, details) {
    var info = details && typeof details === 'object' ? details : {};

    if (kind === 'register') {
      cartForm.snapshot = typeof info.snapshot === 'function' ? info.snapshot : null;
      return true;
    }

    if (!state.initialized) {
      // PostHog loads after the cart script runs; keep early clicks for the load.
      if (pendingCartForm.length < CART_FORM_QUEUE_LIMIT) pendingCartForm.push([kind, info]);
      return true;
    }
    if (!state.analyticsAllowed) return true;

    if (kind === 'field') {
      var field = cleanCartFieldKeys([info.field])[0];
      if (cartForm.started || !field) return false;
      cartForm.started = true;
      cartForm.startedAt = eventTime(info);
      return capture('DG Cart Form Started', {
        first_field: field,
        delivery_mode: cartDeliveryMode(info)
      });
    }

    if (kind === 'blocked') {
      var reason = CART_BLOCK_REASONS.indexOf(info.reason) !== -1 ? info.reason : 'other';
      cartForm.blocks += 1;
      cartForm.lastBlockReason = reason;
      cartForm.passed = false;
      var blocked = {
        block_reason: reason,
        delivery_mode: cartDeliveryMode(info),
        attempt: cartForm.blocks,
        fields_filled_count: cleanCartFieldKeys(info.filled).length
      };
      if (reason === 'missing_fields') blocked.missing_fields = cleanCartFieldKeys(info.missing);
      if (reason === 'heavy_minimum' && isFinite(Number(info.amountMissing))) {
        blocked.amount_missing = Math.max(0, Math.round(Number(info.amountMissing)));
      }
      return capture('DG Checkout Blocked', blocked);
    }

    if (kind === 'passed') {
      cartForm.passed = true;
      var passedAt = eventTime(info);
      return capture('DG Checkout Gate Passed', {
        delivery_mode: cartDeliveryMode(info),
        delivery_state: cartDeliveryState(info.deliveryState),
        blocked_before: cartForm.blocks,
        seconds_on_form: cartForm.startedAt ? Math.max(0, Math.round((passedAt - cartForm.startedAt) / 1000)) : undefined
      });
    }

    if (kind === 'delivery_failed') {
      return capture('DG Delivery Check Failed', {
        failure_reason: DELIVERY_CHECK_FAILURES.indexOf(info.reason) !== -1 ? info.reason : 'other'
      });
    }

    return false;
  }

  function drainCartFormQueue() {
    var queued = Array.isArray(root.__dgCartFormQueue) ? root.__dgCartFormQueue.splice(0) : [];
    var pending = pendingCartForm.splice(0);
    queued.concat(pending).forEach(function (entry) {
      if (Array.isArray(entry)) trackCartForm(entry[0], entry[1]);
    });
  }

  function reportCartFormAbandon() {
    if (!state.initialized || !state.analyticsAllowed) return false;
    if (cartForm.passed || cartForm.abandonReported) return false;
    if (!cartForm.started && !cartForm.blocks) return false;

    var snapshot = {};
    try {
      snapshot = cartForm.snapshot ? (cartForm.snapshot() || {}) : {};
    } catch (error) {
      snapshot = {};
    }
    cartForm.abandonReported = true;
    // Sent by beacon: PostHog flushes its own queue on pagehide before this runs.
    return capture('DG Cart Form Abandoned', {
      delivery_mode: cartDeliveryMode(snapshot),
      fields_filled: cleanCartFieldKeys(snapshot.filled),
      delivery_state: cartDeliveryState(snapshot.deliveryState),
      blocked_attempts: cartForm.blocks,
      last_block_reason: cartForm.lastBlockReason || 'none',
      seconds_on_form: cartForm.startedAt ? Math.max(0, Math.round((Date.now() - cartForm.startedAt) / 1000)) : 0
    }, { transport: 'sendBeacon' });
  }

  function captureThemeDiagnostic(entry) {
    if (!entry || typeof entry !== 'object') return false;
    return capture('DG Component Failure', {
      component: redactText(String(entry.component || 'unknown')).slice(0, 80),
      reason: redactText(String(entry.reason || 'unknown')).slice(0, 80),
      detail: entry.detail ? redactText(String(entry.detail)).slice(0, 160) : undefined
    });
  }

  function attachThemeDiagnostics() {
    if (!root.DGThemeDiagnostics || typeof root.DGThemeDiagnostics.drain !== 'function') return;
    root.DGThemeDiagnostics.drain(captureThemeDiagnostic);
  }

  function handleResourceError(event) {
    var target = event && event.target;
    var pageUrl = root.location && root.location.href ? root.location.href : '';
    if (!target || target === root || !isActionableResourceElement(target, pageUrl)) return;
    var resourceUrl = target.currentSrc || target.src || target.href || '';
    capture('DG Resource Load Failed', {
      resource_type: String(target.tagName).toLowerCase().slice(0, 40),
      resource_url: resourceUrl ? sanitizeUrl(String(resourceUrl)) : undefined
    });
  }

  // One summary when the shopper leaves or hides the page, and only when there
  // was new activity since the last one (a hide followed by the page unloading
  // is one visit, not two). The old 60-second interval copies are gone: they
  // cost a tenth of the monthly event quota and repeated the same numbers.
  function captureEngagementSummary(reason) {
    var newActivity = state.activeSeconds > state.lastSummaryActiveSeconds ||
      state.meaningfulClicks > state.lastSummaryClicks;
    if (!newActivity) return false;
    if (state.activeSeconds < 20 && state.meaningfulClicks < 2) return false;

    // Leaving summaries go by beacon: PostHog drains its batch queue on pagehide
    // before this listener runs, so a queued event would be lost with the page.
    var properties = Object.assign(engagementProperties(), { summary_reason: reason });
    if (state.opaqueScriptErrors) properties.opaque_script_errors = state.opaqueScriptErrors;
    capture('DG Engagement Summary', properties, { transport: 'sendBeacon' });
    state.lastSummaryActiveSeconds = state.activeSeconds;
    state.lastSummaryClicks = state.meaningfulClicks;
    return true;
  }

  function tickActiveTime() {
    if (!state.analyticsAllowed || documentRef.visibilityState !== 'visible') return;
    if (Date.now() - state.lastInteractionAt > 30000) return;
    state.activeSeconds += 5;
    persistSessionState();
    evaluateHighIntent();
  }

  function attachListeners() {
    if (state.listenersAttached || !documentRef) return;
    state.listenersAttached = true;
    documentRef.addEventListener('click', handleClick, true);
    documentRef.addEventListener('click', handleRageClick, true);
    documentRef.addEventListener('submit', handleSubmit, true);
    documentRef.addEventListener('keydown', function () {
      if (!state.analyticsAllowed) return;
      state.lastInteractionAt = Date.now();
    }, { passive: true });
    var lastPointerActivityAt = 0;
    documentRef.addEventListener('pointermove', function () {
      if (!state.analyticsAllowed) return;
      var now = Date.now();
      if (now - lastPointerActivityAt < 5000) return;
      lastPointerActivityAt = now;
      state.lastInteractionAt = now;
    }, { passive: true });
    documentRef.addEventListener('visibilitychange', function () {
      if (!state.analyticsAllowed) return;
      if (documentRef.visibilityState === 'hidden') captureEngagementSummary('hidden');
      else state.lastInteractionAt = Date.now();
    });
    root.addEventListener('scroll', handleScroll, { passive: true });
    root.addEventListener('error', handleResourceError, true);
    root.addEventListener('dg:delivery-quote', handleDeliveryQuote);
    root.addEventListener('pagehide', function () {
      if (!state.analyticsAllowed) return;
      reportCartFormAbandon();
      captureEngagementSummary('pagehide');
      persistSessionState();
    });
    root.addEventListener('pageshow', function (event) {
      // Back from checkout restores this page from the back/forward cache: a later
      // exit without paying is a new abandonment, not the one already reported.
      if (event && event.persisted) cartForm.abandonReported = false;
    });
    root.setInterval(tickActiveTime, 5000);
    attachThemeDiagnostics();
    patchFetch();
  }

  function onPostHogLoaded(posthog) {
    state.initialized = true;
    state.initializing = false;
    markVisitorType();
    readSessionState();
    state.purchasedRecently = hasRecentPurchase();

    posthog.register(Object.assign(visitorProperties(), campaignProperties()));
    posthog.register_once({
      dg_initial_page_type: state.config.pageType || 'unknown',
      dg_initial_path: sanitizeUrl(root.location.href)
    });
    identifyCustomer();
    linkShopifyIdentity(writeDistinctIdBridge());
    attachListeners();
    trackNewsletterPage(root, documentRef, capture, Date.now());
    if (typeof posthog.getSurveys === 'function') {
      posthog.getSurveys(function () {});
    }
    sendInitialEvents();
    drainCartFormQueue();
    linkClaritySession(root);
    root.dispatchEvent(new root.Event('dg:analytics-state'));
  }

  function initializePostHog() {
    if (state.initialized || state.initializing || !state.analyticsAllowed || !state.config) return;
    state.initializing = true;
    var posthog = installPostHogStub();

    posthog.init(state.config.projectToken, {
      api_host: state.config.apiHost,
      ui_host: state.config.uiHost,
      defaults: '2026-05-30',
      // Off to stay inside the free 1M events/month (projected 1.57M with them on):
      // DG Meaningful Click + DG Rage Click cover clicks; Clarity keeps heatmaps.
      autocapture: false,
      capture_pageview: true,
      capture_pageleave: true,
      capture_dead_clicks: true,
      capture_exceptions: true,
      capture_heatmaps: false,
      capture_performance: true,
      disable_session_recording: true,
      disable_surveys: false,
      advanced_enable_surveys: true,
      enable_recording_console_log: false,
      person_profiles: 'identified_only',
      persistence: 'localStorage+cookie',
      respect_dnt: true,
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: MASK_TEXT_SELECTOR,
        blockSelector: BLOCK_SELECTOR,
        recordCrossOriginIframes: false,
        maskCapturedNetworkRequestFn: function (request) {
          if (request && request.name) request.name = sanitizeUrl(request.name);
          return request;
        }
      },
      before_send: beforeSend,
      loaded: onPostHogLoaded
    });
  }

  function revokeAnalytics() {
    clearDistinctIdBridge();
    safeStorageRemove(root.localStorage, FIRST_SEEN_KEY);
    clearPurchaseBridge();
    safeStorageRemove(root.localStorage, CUSTOMER_KEY);
    safeStorageRemove(root.sessionStorage, SESSION_KEY);
    safeStorageRemove(root.sessionStorage, HIGH_INTENT_KEY);
    safeStorageRemove(root.sessionStorage, DELIVERY_QUOTE_KEY);

    if (!state.initialized || !root.posthog) {
      return;
    }

    if (typeof root.posthog.stopSessionRecording === 'function') root.posthog.stopSessionRecording();
    if (typeof root.posthog.opt_out_capturing === 'function') {
      root.posthog.opt_out_capturing({ clear_persistence: true });
    }
    state.activeSeconds = 0;
    state.meaningfulClicks = 0;
    state.productViews = 0;
    state.searches = 0;
    state.addToCart = 0;
    state.checkoutClicks = 0;
    state.pageviews = 0;
    state.maxScrollDepth = 0;
    state.purchasedRecently = false;
    state.highIntentFired = false;
    state.metaHighIntentSent = false;
    state.highIntentEventId = null;
    state.highIntentReason = null;
    state.initialEventsSent = false;
    var snapshot = cartForm.snapshot;
    cartForm = freshCartFormState();
    cartForm.snapshot = snapshot;
    pendingCartForm = [];
  }

  function syncConsent() {
    var consent = readConsent();
    var analyticsWasAllowed = state.analyticsAllowed;
    state.analyticsAllowed = consent.analytics;
    state.marketingAllowed = consent.marketing;

    if (!state.analyticsAllowed) {
      revokeAnalytics();
      root.dispatchEvent(new root.Event('dg:analytics-state'));
      return;
    }

    if (!analyticsWasAllowed && state.initialized && root.posthog) {
      root.posthog.opt_in_capturing();
      markVisitorType();
      root.posthog.register(Object.assign(visitorProperties(), campaignProperties()));
      identifyCustomer();
      linkShopifyIdentity(writeDistinctIdBridge());
      sendInitialEvents();
      drainCartFormQueue();
      linkClaritySession(root);
    } else {
      initializePostHog();
    }

    evaluateHighIntent();
    root.dispatchEvent(new root.Event('dg:analytics-state'));
  }

  function boot() {
    if (!documentRef) return;
    state.config = parseConfig();
    if (!state.config || !state.config.projectToken) return;

    documentRef.addEventListener('visitorConsentCollected', function () {
      syncConsent();
    });
    ensurePrivacyApi(syncConsent);
  }

  function status() {
    return {
      initialized: state.initialized,
      analyticsAllowed: state.analyticsAllowed,
      marketingAllowed: state.marketingAllowed,
      activeSeconds: state.activeSeconds,
      meaningfulClicks: state.meaningfulClicks,
      productViews: state.productViews,
      searches: state.searches,
      addToCart: state.addToCart,
      checkoutClicks: state.checkoutClicks,
      maxScrollDepth: state.maxScrollDepth,
      intentScore: calculateIntentScore(state),
      highIntent: state.highIntentFired,
      metaHighIntentSent: state.metaHighIntentSent,
      purchasedRecently: state.purchasedRecently
    };
  }

  return {
    boot: boot,
    capture: capture,
    status: status,
    sanitizeUrl: sanitizeUrl,
    redactText: redactText,
    sanitizeEvent: sanitizeEvent,
    wrapShopifyMetricsRetry: wrapShopifyMetricsRetry,
    isEmptySyntheticException: isEmptySyntheticException,
    isInjectedBridgeException: isInjectedBridgeException,
    isInfiniteScrollClickNoise: isInfiniteScrollClickNoise,
    isActionableResourceUrl: isActionableResourceUrl,
    isActionableResourceElement: isActionableResourceElement,
    interactionLabel: interactionLabel,
    deliveryCostBucket: deliveryCostBucket,
    deliveryQuoteProperties: deliveryQuoteProperties,
    cookieValue: cookieValue,
    recentPurchaseTimestamp: recentPurchaseTimestamp,
    publishShopifyIdentity: publishShopifyIdentity,
    scheduleShopifyIdentityReplay: scheduleShopifyIdentityReplay,
    calculateIntentScore: calculateIntentScore,
    highIntentReason: highIntentReason,
    trackNewsletterStart: trackNewsletterStart,
    trackNewsletterPage: trackNewsletterPage,
    trackCartForm: trackCartForm,
    mirrorToClarity: mirrorToClarity,
    linkClaritySession: linkClaritySession,
    checkoutButtonLocation: checkoutButtonLocation,
    shouldPublishIdentity: shouldPublishIdentity
  };
});
